import { createFileRoute, Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useLang } from "@/hooks/use-language";
import { ArrowLeft, Clock, Star, BookOpen, Lock } from "lucide-react";
import { coverFor } from "@/assets/worlds";
import { useSmartBack } from "@/hooks/use-smart-back";
import { getStoriesForChild } from "@/lib/content.functions";

export const Route = createFileRoute("/_authenticated/app/child/$childId/stories")({
  component: StoriesRouteShell,
});

type StoryRow = {
  id: string;
  title_ar: string;
  title_en: string;
  description_ar: string | null;
  description_en: string | null;
  cover_url: string | null;
  world_id: string | null;
  slug: string | null;
  age_group: string | null;
  character_code: string | null;
  character_avatar: string | null;
  tier: string;
  reading_minutes: number | null;
};

function tierUnlocks(storyTier: string, userTier: "free" | "family" | "premium") {
  if (storyTier === "free") return true;
  if (storyTier === "family") return userTier === "family" || userTier === "premium";
  if (storyTier === "premium") return userTier === "premium";
  return false;
}

function StoriesRouteShell() {
  const { childId } = Route.useParams();
  const { pathname } = useLocation();
  const isList = pathname === `/app/child/${childId}/stories`;

  return isList ? <StoriesList /> : <Outlet />;
}

function StoriesList() {
  const { childId } = Route.useParams();
  const navigate = useNavigate();
  const goBack = useSmartBack(() => navigate({ to: "/app/child/$childId", params: { childId } }));
  const { lang, t, dir } = useLang();
  const fetchStories = useServerFn(getStoriesForChild);
  const isAr = lang === "ar";

  const {
    data: storyPayload,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ["stories", "list", childId],
    queryFn: () => fetchStories({ data: { childId } }),
  });
  const items = (storyPayload?.items ?? []) as unknown as StoryRow[];
  const userTier = (storyPayload?.userTier ?? "free") as "free" | "family" | "premium";

  const unlocked = items.filter((s) => tierUnlocks(s.tier, userTier));
  const locked = items.filter((s) => !tierUnlocks(s.tier, userTier));

  return (
    <div className="px-5 py-6 max-w-2xl mx-auto" dir={dir}>
      <button
        onClick={goBack}
        className="flex items-center gap-2 text-sm font-bold text-foreground/60 mb-4 hover:text-foreground"
      >
        <ArrowLeft className={`size-4 ${isAr ? "rotate-180" : ""}`} /> {t("backToHome")}
      </button>
      <h1
        className={`${isAr ? "font-display-ar" : "font-display-en"} text-3xl font-bold tracking-tight mb-1`}
      >
        {isAr ? "القصص" : t("stories")}
      </h1>
      <p className="text-foreground/60 mb-6 text-sm">
        {loading
          ? isAr
            ? "جارٍ التحميل…"
            : "Loading…"
          : isAr
            ? `${items.length} قصة في المكتبة`
            : `${items.length} stories in the library`}
      </p>

      {error && (
        <div className="rounded-3xl border-2 border-dashed border-clay/40 p-8 text-center">
          <BookOpen className="mx-auto mb-3 size-8 text-foreground/40" />
          <p className="text-foreground/60 text-sm">
            {isAr
              ? "تعذّر فتح القصص الآن. حاول مرة أخرى بهدوء."
              : "Stories could not load right now. Please try again."}
          </p>
        </div>
      )}

      {loading && (
        <div className="grid gap-4">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="bg-card border border-clay/20 rounded-3xl overflow-hidden animate-pulse"
            >
              <div className="aspect-[16/9] bg-clay/20" />
              <div className="p-4 space-y-2">
                <div className="h-4 w-2/3 bg-clay/20 rounded" />
                <div className="h-3 w-1/2 bg-clay/15 rounded" />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="rounded-3xl border-2 border-dashed border-clay/40 p-8 text-center">
          <BookOpen className="mx-auto mb-3 size-8 text-foreground/40" />
          <p className="text-foreground/60 text-sm">
            {isAr ? "ستظهر قصص جديدة قريبًا" : "New stories coming soon"}
          </p>
        </div>
      )}

      {unlocked.length > 0 && (
        <>
          <h2
            className={`${isAr ? "font-display-ar" : "font-display-en"} text-sm uppercase tracking-widest text-foreground/50 font-bold mb-3 mt-2`}
          >
            {isAr ? "متاح لك" : "Available to read"}
          </h2>
          <div className="grid gap-4 mb-8">
            {unlocked.map((s) => (
              <StoryCard key={s.id} story={s} locked={false} isAr={isAr} ptsLabel={t("pts")} />
            ))}
          </div>
        </>
      )}

      {locked.length > 0 && (
        <>
          <h2
            className={`${isAr ? "font-display-ar" : "font-display-en"} text-sm uppercase tracking-widest text-foreground/50 font-bold mb-1 mt-2 flex items-center gap-2`}
          >
            <Lock className="size-3.5" />
            {isAr ? "تتفتح مع الاشتراك" : "Unlocks with subscription"}
          </h2>
          <p className="text-xs text-foreground/50 mb-3">
            {isAr
              ? "تصفّح القصص الكاملة، وافتح كلها بخطة العائلة."
              : "Browse the full library — unlock all with the Family plan."}
          </p>
          <div className="grid gap-4">
            {locked.map((s) => (
              <StoryCard key={s.id} story={s} locked={true} isAr={isAr} ptsLabel={t("pts")} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StoryCard({
  story: s,
  locked,
  isAr,
  ptsLabel,
}: {
  story: StoryRow;
  locked: boolean;
  isAr: boolean;
  ptsLabel: string;
}) {
  const title = isAr ? s.title_ar : s.title_en;
  const subtitle = isAr ? s.title_en : s.title_ar;
  const desc = isAr ? s.description_ar : s.description_en;
  const cover = coverFor({
    cover_url: s.cover_url,
    world_id: s.world_id,
    age_group: s.age_group,
    slug: s.slug,
    character_code: s.character_code,
    character_avatar: s.character_avatar,
  });
  const fallbackClass = (() => {
    const code = s.id.charCodeAt(0) % 4;
    if (code === 0) return "from-primary/25 via-card to-accent/20";
    if (code === 1) return "from-secondary/25 via-card to-primary/15";
    if (code === 2) return "from-accent/25 via-card to-secondary/15";
    return "from-clay/35 via-card to-primary/15";
  })();

  const inner = (
    <>
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-gradient-to-br from-primary/15 via-accent/15 to-secondary/15">
        {cover ? (
          <img
            src={cover}
            alt={title}
            width={672}
            height={378}
            className={`absolute inset-0 h-full w-full object-cover transition-transform duration-500 ${locked ? "" : "group-hover:scale-[1.03]"}`}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div
            className={`absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br ${fallbackClass} p-5 text-center`}
          >
            <BookOpen className="size-12 text-primary/80" />
            <span
              className={`${isAr ? "font-display-ar" : "font-display-en"} text-sm font-bold leading-snug text-foreground/80`}
            >
              {title}
            </span>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-foreground/30 to-transparent pointer-events-none" />
        {locked && (
          <div className="absolute inset-0 bg-foreground/35 backdrop-blur-[1px] flex flex-col items-center justify-center gap-1 text-card">
            <Lock className="size-7" />
            <span className="text-[11px] font-bold uppercase tracking-wider">
              {isAr ? "ضمن خطة العائلة" : "Family plan"}
            </span>
          </div>
        )}
      </div>
      <div className="p-4">
        {subtitle && (
          <p
            className={`text-[11px] text-foreground/50 mb-0.5 ${isAr ? "" : "font-display-ar"}`}
            dir={isAr ? "ltr" : "rtl"}
          >
            {subtitle}
          </p>
        )}
        <h3
          className={`text-base sm:text-lg font-bold mb-1 leading-snug ${isAr ? "font-display-ar" : "font-display-en"}`}
        >
          {title}
        </h3>
        {desc && (
          <p className="text-[13px] text-foreground/60 line-clamp-2 mb-2 leading-relaxed">{desc}</p>
        )}
        <div className="flex items-center gap-3 text-xs text-foreground/60">
          {s.reading_minutes ? (
            <span className="flex items-center gap-1">
              <Clock className="size-3" /> {s.reading_minutes} {isAr ? "د" : "min"}
            </span>
          ) : null}
          <span className="flex items-center gap-1 text-primary font-bold">
            <Star className="size-3" /> +10 {ptsLabel}
          </span>
        </div>
      </div>
    </>
  );

  if (locked) {
    return (
      <Link
        to="/app/pricing"
        className="group block bg-card border border-clay/30 rounded-3xl overflow-hidden hover:border-accent/60 transition-all"
      >
        {inner}
      </Link>
    );
  }

  return (
    <Link
      to="/app/watch/$id"
      params={{ id: s.id }}
      className="group block bg-card border border-clay/30 rounded-3xl overflow-hidden hover:border-primary/60 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 active:scale-[0.99] transition-all duration-300"
    >
      {inner}
    </Link>
  );
}
